import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { authService } from '@/domains/internal/auth/services/authService';
import { rememberMeUtils } from '@/utils/rememberMe';
import { Button } from '@/shared/components/ui';
import { useAuth } from '@/shared/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import logoVector1 from '@/assets/logo-vector.svg';
import logoVector2 from '@/assets/logo-vector-2.svg';
import logoVector3 from '@/assets/logo-vector-3.svg';

const Login: React.FC = () => {
  const { refreshAuth } = useAuth();
  const { theme, tenant, loading: themeLoading } = useTheme();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/dashboard';

  // Load saved username on mount
  useEffect(() => {
    const savedUsername = rememberMeUtils.getSavedUsername();
    if (savedUsername) {
      setEmail(savedUsername);
      setRememberMe(true);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const response = await authService.login({ email, password });

      // Handle Remember Me - save or clear username
      if (rememberMe) {
        rememberMeUtils.saveUsername(email);
      } else {
        rememberMeUtils.clearSavedUsername();
      }

      // Refresh AuthContext to notify it that user is now authenticated
      await refreshAuth();

      // Check if user must change password
      if (response.must_change_password) {
        // Store flag so other components (like WebSocket) know to wait
        localStorage.setItem('must_change_password', 'true');
        navigate('/change-password', { replace: true });
      } else {
        localStorage.removeItem('must_change_password');
        navigate(from, { replace: true });
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  // Show minimal loading state while theme loads
  if (themeLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-400 mx-auto"></div>
          <p className="mt-4 text-sm text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-white py-12 px-4 sm:px-6 lg:px-8">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-xl shadow-lg px-8 py-6">
          {/* Logo and Header */}
          <div className="flex flex-col gap-5 mb-8">
            <div className="flex justify-center">
              <div className="text-center">
                {theme?.logo_url ? (
                  // Tenant logo from theme
                  <div className="mx-auto mb-2">
                    <img
                      src={theme.logo_url}
                      alt={tenant?.name || 'Logo'}
                      className="h-24 w-auto mx-auto"
                    />
                    {tenant?.name && (
                      <p className="mt-2 text-sm tracking-wide" style={{ color: 'var(--color-primary)' }}>
                        {tenant.name}
                      </p>
                    )}
                  </div>
                ) : (
                  // Default logo with themed color
                  <div className="relative w-[138px] h-[94px] mx-auto">
                    <img
                      src={logoVector1}
                      alt="Logo part 1"
                      className="absolute top-0 left-[34.984px] w-[68.735px] h-[67.681px]"
                      style={{ fill: 'var(--color-primary, #CCA352)' }}
                    />
                    <img
                      src={logoVector2}
                      alt="Logo part 2"
                      className="absolute top-[3.462px] left-[23.639px] w-[84.059px] h-[59.822px]"
                      style={{ fill: 'var(--color-primary, #CCA352)' }}
                    />
                    <img
                      src={logoVector3}
                      alt="Logo part 3"
                      className="absolute top-[56.581px] left-[22.772px] w-[21.409px] h-[11.076px]"
                      style={{ fill: 'var(--color-primary, #CCA352)' }}
                    />
                    <div className="absolute top-[75.822px] left-0 w-full text-[7.736px] tracking-wide" style={{ color: 'var(--color-primary, #CCA352)' }}>
                      ANAND IMAGING & DIAGNOSTICS
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-6">
              {/* Username Field - accepts Employee ID, Email, or Phone */}
              <div className="flex flex-col gap-1">
                <label
                  htmlFor="email"
                  className="text-sm font-medium text-[var(--color-text-primary)] tracking-wide"
                >
                  Employee ID, Email, or Phone
                </label>
                <div className="relative">
                  <div className="absolute left-3.5 top-1/2 -translate-y-1/2">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <circle cx="10" cy="7" r="3.5" stroke="#ADAAAA" strokeWidth="1.5"/>
                      <path d="M4 17c0-2.5 2.5-5 6-5s6 2.5 6 5" stroke="#ADAAAA" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                  </div>
                  <input
                    id="email"
                    name="email"
                    type="text"
                    autoComplete="username"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Employee ID, email, or phone number"
                    className="w-full pl-11 pr-3.5 py-3 border border-[#e5e2e1] rounded-lg text-sm text-[var(--color-text-primary)] placeholder-[var(--color-text-muted)] focus:outline-none focus:ring-2 focus:border-transparent"
                    style={{ '--tw-ring-color': 'var(--color-primary)' } as React.CSSProperties}
                  />
                </div>
              </div>

              {/* Password Field */}
              <div className="flex flex-col gap-1">
                <label
                  htmlFor="password"
                  className="text-sm font-medium text-[var(--color-text-primary)] tracking-wide"
                >
                  Password
                </label>
                <div className="relative">
                  <div className="absolute left-3.5 top-1/2 -translate-y-1/2">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <circle cx="10" cy="7" r="3.5" stroke="#ADAAAA" strokeWidth="1.5"/>
                      <path d="M4 17c0-2.5 2.5-5 6-5s6 2.5 6 5" stroke="#ADAAAA" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                  </div>
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    className="w-full pl-11 pr-11 py-3 border border-[#e5e2e1] rounded-lg text-sm text-[var(--color-text-primary)] placeholder-[var(--color-text-muted)] focus:outline-none focus:ring-2 focus:border-transparent"
                    style={{ '--tw-ring-color': 'var(--color-primary)' } as React.CSSProperties}
                  />
                  <Button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    variant="neutral"
                    className="!absolute !right-3.5 !top-1/2 !-translate-y-1/2 !text-[var(--color-text-muted)] hover:!text-[var(--color-text-primary)] !bg-transparent !border-none !p-0 !h-auto"
                    padding="p-0"
                  >
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      {showPassword ? (
                        <>
                          <path d="M2 2L18 18M9.5 9.5C9 10 9 11 9.5 11.5C10 12 11 12 11.5 11.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                          <path d="M6.5 6.5C4.5 7.5 3 9.5 3 10C3 10.5 6 15 10 15C11 15 12 14.8 13 14.3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                          <path d="M17 10C17 9.5 14 5 10 5C9.5 5 9 5.1 8.5 5.2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        </>
                      ) : (
                        <>
                          <path d="M10 13C11.6569 13 13 11.6569 13 10C13 8.34315 11.6569 7 10 7C8.34315 7 7 8.34315 7 10C7 11.6569 8.34315 13 10 13Z" stroke="currentColor" strokeWidth="1.5"/>
                          <path d="M3 10C3 10 6 5 10 5C14 5 17 10 17 10C17 10 14 15 10 15C6 15 3 10 3 10Z" stroke="currentColor" strokeWidth="1.5"/>
                        </>
                      )}
                    </svg>
                  </Button>
                </div>
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            {/* Remember me and Forgot password */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="h-4 w-4 rounded border-[#e6e2df]"
                  style={{
                    color: 'var(--color-primary)',
                    '--tw-ring-color': 'var(--color-primary)'
                  } as React.CSSProperties}
                />
                <label
                  htmlFor="remember-me"
                  className="text-sm font-medium text-[var(--color-text-primary)] tracking-wide"
                >
                  Remember me
                </label>
              </div>
              <Link
                to="/forgot-password"
                className="text-sm text-[#4a90e2] hover:underline tracking-wide"
              >
                Forgot your password?
              </Link>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={isLoading}
              variant="primary"
              fullWidth
              padding="py-3 px-6"
              fontSize="text-base"
              className="tracking-wide focus:ring-2 focus:ring-offset-2"
              style={{ '--tw-ring-color': 'var(--color-primary)' } as React.CSSProperties}
            >
              {isLoading ? 'Signing in...' : 'Log in'}
            </Button>
          </form>

          {/* Referral Portal Link */}
          <div className="mt-6 pt-6 border-t border-[#e5e2e1]">
            <p className="text-center text-sm text-[var(--color-text-muted)]">
              Referral Partner?{' '}
              <Link to="/referral-portal/login" className="hover:underline font-medium" style={{ color: 'var(--color-primary)' }}>
                Access Referral Portal
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;